import React from 'react'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'

import { Loading } from '../Loading'
import { useAlert } from '../../hooks'
import useUser from '../../hooks/useUser'
import { useAppContext } from '../AppContext'
import queryKeys from '../../services/queryKeys'
import AccountForm from '../../forms/AccountForm'
import { eventTypes } from '../../services/constants'
import { useMixpanel } from '../../hooks/useMixpanel'
import { getErrorMessage } from '../../services/helper'
import { useAppContextActions } from '../AppContext/AppHooks'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'

const AccountSettings = () => {
	const { alert } = useAlert()
	const mixpanel = useMixpanel()
	const { applySiteConfig } = useAppContextActions()
	const { state } = useAppContext()
	const { siteConfig, socialLinks, legalLinks } = state
	const { user, setUser } = useUser()

	const queryClient = useQueryClient()
	const {
		isPending,
		error,
		data: account,
	} = useQuery({
		queryKey: [queryKeys.account],
		queryFn: TheBigPOSApi.getMyAccount,
	})

	if (error) {
		alert(getErrorMessage(error), { severity: 'error' })
	}

	const accountMutation = useMutation({
		mutationFn: TheBigPOSApi.replaceMyAccount,
	})

	const save = async (data) => {
		try {
			const { mfaPreference, asoSettings } = data
			await accountMutation.mutateAsync(data)
			mixpanel.trackEvent(eventTypes.ACCOUNT_SETTINGS_UPDATED, {
				mfaPreference,
				asoSettings,
			})
			alert('Account settings updated', { severity: 'success' })
			await queryClient.invalidateQueries({
				queryKey: [queryKeys.account],
			})
			if (mfaPreference === 'Required' && !user.mfaEnabled) {
				// Add skip MFA so the header still displays if current user doesn't have MFA enabled.
				// See comments in HeaderNavigation above showNavigation for more info
				setUser({ ...user, skippedMFAConfig: true })
			}
			siteConfig.mfaPreference = mfaPreference
			applySiteConfig({ siteConfig, socialLinks, legalLinks })
		} catch (e) {
			alert(getErrorMessage(e), { severity: 'error' })
		}
	}

	if (isPending || !account?.data) {
		return <Loading size="small" />
	}

	return (
		<div className="w-full md:mb-0">
			<AccountForm account={account.data} onSubmit={save} />
		</div>
	)
}

export default AccountSettings
